import React from 'react'

import { ApolloError, isApolloError } from 'apollo-boost'
import { Stack } from 'component-library-next'
import { GraphQLError } from 'graphql'
import { ApplicationErrorCode } from 'service-manager'

import { logger } from '@app/common/logger/logger'
import { useIntl } from '@app/intl/Intl'

export type CustomApolloError = ApolloError & {
  graphQLErrors: Readonly<
    GraphQLError & {
      extensions: {
        code:
          | 'SYNTAX_ERROR'
          | 'VALIDATION_ERROR'
          | 'USER_INPUT_ERROR'
          | 'AUTHENTICATION_ERROR'
          | 'FORBIDDEN_ERROR'
          | string
        errors: Array<{
          code: ApplicationErrorCode
          context?: Record<string, any>
        }>
      }
    }
  >
}

export type ApplicationError = Error & {
  code: ApplicationErrorCode
  context?: Record<string, any>
}

export type FormattedErrorProps = {
  error?: CustomApolloError | ApolloError | Error | ApplicationError
}

const parseError = (error: CustomApolloError | ApolloError, formatMessage): string[] => {
  logger.log('Apollo error', { ...error })

  const messages: string[] = []

  if (error.networkError) {
    messages.push(formatMessage({ id: 'Errors.GraphQL.NetworkError' }))
  }

  if (error.graphQLErrors.length) {
    error.graphQLErrors.forEach((graphQLError) => {
      switch (graphQLError?.extensions?.code) {
        case 'SYNTAX_ERROR':
          messages.push(formatMessage({ id: 'Errors.GraphQL.SyntaxError' }))
          break

        case 'VALIDATION_ERROR':
          messages.push(formatMessage({ id: 'Errors.GraphQL.ValidationError' }))
          break

        case 'USER_INPUT_ERROR':
          messages.push(formatMessage({ id: 'Errors.GraphQL.UserInputError' }))
          break

        case 'AUTHENTICATION_ERROR':
          messages.push(formatMessage({ id: 'Errors.GraphQL.AuthenticationError' }))
          break

        case 'FORBIDDEN_ERROR':
          messages.push(formatMessage({ id: 'Errors.GraphQL.ForbiddenError' }))
          break

        default:
          if (graphQLError?.extensions?.errors) {
            messages.push(
              graphQLError.extensions.errors.map((err) =>
                formatMessage({ id: `Errors.${err.code}` }, err.context || {})
              )
            )
            break
          }

          messages.push(formatMessage({ id: 'Errors.Server.Runtime.UnspecifiedError' }))
          break
      }
    })
  }

  return messages.filter(Boolean)
}

export const FormattedError = ({ error }: FormattedErrorProps) => {
  const { formatMessage, messages } = useIntl()
  if (!error) {
    return <>{formatMessage({ id: 'Errors.Server.Runtime.UnspecifiedError' })}</>
  }

  if (isApolloError(error)) {
    const messages = parseError(error, formatMessage)
    return (
      <Stack>
        {messages.map((message) => (
          <div key={message}>{message}</div>
        ))}
      </Stack>
    )
  }

  const errorCode = (error as any).code
  if (typeof messages[errorCode] !== 'undefined') {
    return <>{formatMessage({ id: `Errors.${errorCode}` as any })}</>
  }

  return error.message ? (
    <>{error.message}</>
  ) : (
    <>{formatMessage({ id: 'Errors.Server.Runtime.UnspecifiedError' })}</>
  )
}
